import { useState } from "react";
import axios from "axios";
import PageHero from "@/components/PageHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  "Care Assistant",
  "Senior Care Assistant",
  "Live-In Carer",
  "Overnight Carer",
  "Care Coordinator",
];

const experiences = [
  "Brand new to care",
  "Less than 1 year",
  "1–3 years",
  "3+ years",
];

const CareersPage = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    role: roles[0],
    experience: experiences[0],
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      setStatus({ state: "error", error: "Name, phone and email are required." });
      return;
    }
    setStatus({ state: "loading", error: "" });
    try {
      await axios.post(`${API}/careers`, form);
      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "career_application_submitted",
          role: form.role,
        });
      }
      setStatus({ state: "success", error: "" });
      setForm({ name: "", phone: "", email: "", role: roles[0], experience: experiences[0], message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        error:
          err?.response?.data?.detail?.[0]?.msg ||
          "Couldn't send your application — please try again or call us.",
      });
    }
  };

  return (
    <main data-testid="page-careers">
      <PageHero
        eyebrow="Join Our Team"
        title="Be the same warm, familiar face — every visit."
        subtitle="At SeaShells we look after our carers the way we look after our clients. Better pay, better training, real teams, and the time to actually care."
        ctaTo="#apply"
        ctaLabel="Apply now"
      />

      <section className="section">
        <div className="container-px max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="eyebrow">Why carers stay with us</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              A care company that treats carers like grown-ups.
            </h2>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                ["fa-coins", "Above-sector pay", "We benchmark above the Essex average — and pay travel time."],
                ["fa-clock", "Real visit times", "1-hour minimum visits. No 15-minute drive-bys, ever."],
                ["fa-graduation-cap", "Proper training", "Fully funded, in-house. Care Certificate, dementia, end-of-life and more."],
                ["fa-people-roof", "Small, kind team", "Family-run. Owners you can call by their first name."],
                ["fa-route", "Local rounds", "We keep your visits clustered close to home where possible."],
                ["fa-mug-hot", "Time to care", "Rota built around relationships, not throughput."],
              ].map(([icon, title, body], i) => (
                <div key={title} className="card-soft" data-testid={`career-perk-${i + 1}`}>
                  <div className="w-11 h-11 rounded-full bg-[var(--ss-cream)] text-[var(--ss-teal-700)] flex items-center justify-center">
                    <i className={`fa-solid ${icon}`}></i>
                  </div>
                  <h3 className="font-serif text-xl text-[var(--ss-teal-800)] mt-4">
                    {title}
                  </h3>
                  <p className="text-[var(--ss-ink-soft)] text-sm mt-2">{body}</p>
                </div>
              ))}
            </div>
          </div>

          <aside id="apply" className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="bg-[var(--ss-cream)] rounded-3xl p-7 border border-[var(--ss-gold)]/30">
              <div className="font-serif text-2xl text-[var(--ss-teal-800)]">
                Apply to join us
              </div>
              <p className="mt-2 text-sm text-[var(--ss-ink-soft)]">
                Quick form — we'll come back to you within 2 working days.
              </p>

              {status.state === "success" ? (
                <div data-testid="career-success" className="mt-5 rounded-2xl bg-white border border-[var(--ss-gold)]/40 p-6 text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-[var(--ss-teal-700)] text-[var(--ss-cream)] flex items-center justify-center text-2xl">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <h3 className="font-serif text-2xl text-[var(--ss-teal-800)] mt-4">
                    Thanks for applying!
                  </h3>
                  <p className="text-[var(--ss-ink-soft)] mt-2 text-sm">
                    We'll review and reach out to arrange a chat very soon.
                  </p>
                  <button
                    className="btn btn-outline-dark mt-5"
                    onClick={() => setStatus({ state: "idle", error: "" })}
                  >
                    Submit another
                  </button>
                </div>
              ) : (
                <form data-testid="career-form" onSubmit={submit} className="mt-5 grid gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
                      Full name *
                    </label>
                    <input data-testid="career-name" className="input" value={form.name} onChange={update("name")} required />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
                        Phone *
                      </label>
                      <input data-testid="career-phone" className="input" value={form.phone} onChange={update("phone")} required />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
                        Email *
                      </label>
                      <input data-testid="career-email" type="email" className="input" value={form.email} onChange={update("email")} required />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
                        Role
                      </label>
                      <select data-testid="career-role" className="input cursor-pointer" value={form.role} onChange={update("role")}>
                        {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
                        Experience
                      </label>
                      <select data-testid="career-experience" className="input cursor-pointer" value={form.experience} onChange={update("experience")}>
                        {experiences.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
                      A few lines about you
                    </label>
                    <textarea data-testid="career-message" className="input min-h-[110px]" value={form.message} onChange={update("message")} />
                  </div>
                  {status.state === "error" && (
                    <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                      <i className="fa-solid fa-circle-exclamation mr-2"></i>
                      {status.error}
                    </div>
                  )}
                  <button data-testid="career-submit" disabled={status.state === "loading"} className="btn btn-primary w-full">
                    {status.state === "loading" ? (
                      <><i className="fa-solid fa-circle-notch fa-spin"></i> Sending...</>
                    ) : (
                      <><i className="fa-regular fa-paper-plane"></i> Send application</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default CareersPage;
